/**
 * Runtime adapter — installs WebMCP tool access on the global window object.
 *
 * Sets `window.__cogo_tools__` with:
 *   - getManifest(): tool definitions (W3C-compatible)
 *   - callTool(name, params): invoke a tool
 *   - getState(path?): current state value (specific path or proxy)
 *   - resolveSymbol(expr): resolve a #{...} expression
 *   - getStateSnapshot(): JSON-serializable full state snapshot
 *   - dispatchEvent(elementId, eventType, payload?): dispatch UI event
 *   - executeFlow(flowName, context?): execute an ActionFlow by name
 *   - ready: boolean indicating runtime is connected
 *
 * This allows external agents (browser extensions, DevTools, AI assistants)
 * to discover and invoke Cogo app tools.
 */

import type { CogoToolRegistry, ToolManifestEntry } from "../core/tool-registry"
import type { WebMCPStateProvider } from "../core/state-provider"

export interface CogoToolsGlobal {
  getManifest(): ToolManifestEntry[]
  callTool(name: string, params: Record<string, unknown>): Promise<unknown>
  getState(path?: string): unknown
  resolveSymbol(expression: string): unknown
  getStateSnapshot(): Record<string, unknown>
  dispatchEvent(elementId: string, eventType: string, payload?: unknown): void
  executeFlow(flowName: string, context?: Record<string, unknown>): Promise<unknown>
  ready: boolean
  /** Debug inspector methods (only available when inspector is enabled). */
  debug?: {
    inspectState(path?: string): unknown
    evaluateExpression(expr: string): unknown
    getFlowTraces(limit?: number): unknown[]
    getCacheStats(): unknown
    clearTraces(): void
  }
}

/** Minimal inspector interface — avoids hard dependency on @cogo/runtime-core. */
export interface InspectorRef {
  enabled: boolean
  getState(): Record<string, unknown>
  getStatePath(path: string): unknown
  evaluateExpression(expr: string): unknown
  getFlowTraces(): unknown[]
  getCacheStats(): unknown
  clearTraces(): void
}

export interface CogoRuntimeRef {
  eventDispatcher?: { dispatch(elementId: string, eventType: string, payload?: unknown): void }
  actionEngine?: {
    executeFlow(flow: unknown, context?: Record<string, unknown>): Promise<void>
    /** v2 ActionFlowEngine provides findFlow for name-based lookup */
    findFlow?(flowName: string): unknown
  }
  flowRegistry?: Record<string, unknown>
  inspector?: InspectorRef
}

export interface CogoDebugAPI {
  state: {
    get(path: string): unknown
    getSnapshot(): Record<string, unknown>
  }
  lastFlow: {
    name: string | null
    status: "idle" | "running" | "completed" | "failed"
    error?: string
  }
}

declare global {
  interface Window {
    __cogo_tools__?: CogoToolsGlobal
    __cogo?: CogoDebugAPI
  }
}

/**
 * Install the WebMCP runtime polyfill on window.__cogo_tools__.
 * Returns a cleanup function.
 */
export function installRuntimePolyfill(
  registry: CogoToolRegistry,
  stateProvider: WebMCPStateProvider,
  runtimeRef?: CogoRuntimeRef,
): () => void {
  if (typeof window === "undefined") {
    // SSR / test environment without DOM — no-op
    return () => {}
  }

  const api: CogoToolsGlobal = {
    getManifest: () => registry.getManifest(),
    callTool: (name, params) => registry.callTool(name, params),
    getState: (path?: string) => {
      if (path) return stateProvider.get(path)
      return stateProvider.getState()
    },
    resolveSymbol: (expr) => stateProvider.resolveSymbol(expr),
    getStateSnapshot: () => stateProvider.getStateSnapshot(),
    dispatchEvent: (elementId, eventType, payload) => {
      if (runtimeRef?.eventDispatcher) {
        runtimeRef.eventDispatcher.dispatch(elementId, eventType, payload)
      } else {
        console.warn("WebMCP: eventDispatcher not available")
      }
    },
    executeFlow: async (flowName, context) => {
      if (!runtimeRef?.actionEngine) {
        console.warn("WebMCP: actionEngine not available")
        return
      }
      // v2: use actionEngine.findFlow() if available (name-based lookup)
      // v1 fallback: use explicit flowRegistry
      let flow: unknown
      if (runtimeRef.actionEngine.findFlow) {
        flow = runtimeRef.actionEngine.findFlow(flowName)
      } else if (runtimeRef.flowRegistry) {
        flow = runtimeRef.flowRegistry[flowName]
      } else {
        console.warn("WebMCP: neither findFlow nor flowRegistry available")
        return
      }
      if (!flow) {
        throw new Error(`WebMCP: flow "${flowName}" not found`)
      }
      // Wrap context in { event: ... } so #{event.*} expressions resolve correctly
      // (bridge-api.ts does this wrapping, but runtime-adapter was missing it)
      const wrappedContext = context ? { event: context } : undefined
      return runtimeRef.actionEngine.executeFlow(flow, wrappedContext)
    },
    ready: true,
  }

  // Attach debug methods if inspector is enabled
  if (runtimeRef?.inspector?.enabled) {
    const insp = runtimeRef.inspector
    api.debug = {
      inspectState: (path) => path ? insp.getStatePath(path) : insp.getState(),
      evaluateExpression: (expr) => insp.evaluateExpression(expr),
      getFlowTraces: (limit) => insp.getFlowTraces().slice(-(limit ?? 10)),
      getCacheStats: () => insp.getCacheStats(),
      clearTraces: () => insp.clearTraces(),
    }
  }

  window.__cogo_tools__ = api

  // Install debug hook (only active when ?__cogo_debug=1)
  const cleanupDebug = installDebugHook({
    getState: (path: string) => api.getState(path),
    getStateSnapshot: () => api.getStateSnapshot(),
  })

  return () => {
    cleanupDebug?.()
    if (window.__cogo_tools__ === api) {
      delete window.__cogo_tools__
    }
  }
}

/**
 * Remove the runtime polyfill from window.
 */
export function uninstallRuntimePolyfill(): void {
  if (typeof window !== "undefined") {
    delete window.__cogo_tools__
  }
}

/**
 * Install the debug API on window.__cogo for narrative verification.
 * Only active when URL contains ?__cogo_debug=1.
 */
export function installDebugHook(apiRef: {
  getState: (path: string) => unknown
  getStateSnapshot: () => Record<string, unknown>
}): (() => void) | null {
  if (typeof window === "undefined") return null

  // Check both current frame and parent frame for debug flag
  // (cogo-starter renders apps in iframes — parent carries the query param)
  let debugEnabled = false
  try {
    const params = new URLSearchParams(window.location.search)
    debugEnabled = params.get("__cogo_debug") === "1"
  } catch { /* cross-origin */ }
  if (!debugEnabled) {
    try {
      const parentParams = new URLSearchParams(window.parent?.location?.search ?? "")
      debugEnabled = parentParams.get("__cogo_debug") === "1"
    } catch { /* cross-origin parent */ }
  }
  if (!debugEnabled) return null

  const lastFlow = {
    name: null as string | null,
    status: "idle" as const,
    error: undefined as string | undefined,
  }

  window.__cogo = {
    state: {
      get: (path: string) => apiRef.getState(path),
      getSnapshot: () => apiRef.getStateSnapshot(),
    },
    lastFlow,
  }

  return () => {
    delete window.__cogo
  }
}

/**
 * Notify the debug hook that a flow started/completed/failed.
 * No-op if debug hook is not installed.
 */
export function notifyFlowStatus(
  name: string,
  status: "running" | "completed" | "failed",
  error?: string,
): void {
  if (typeof window === "undefined" || !window.__cogo) return
  window.__cogo.lastFlow = { name, status, error }
}
